import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { workService } from '../services/workService';
import { WorkStatusBadge } from '../components/work/WorkStatusBadge';
import { PriorityChip } from '../components/work/PriorityChip';
import { ProgressBar } from '../components/work/ProgressBar';
import { DueDateLabel } from '../components/work/DueDateLabel';
import UserBadge from '../components/common/UserBadge';

// TODO: Full implementation in A2 Task 6 — metrics strip, project grid, entity refs
const GoalDetail: React.FC = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const { darkMode } = useTheme();
  const { data: goal, isLoading } = useQuery(['goal', goalId], () => workService.getGoal(goalId!), { enabled: !!goalId });

  if (isLoading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>;
  if (!goal) return <div className="py-20 text-center text-gray-400">Goal not found</div>;

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className={`border-l-4 border-l-emerald-500 rounded-r-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white text-2xl shrink-0">
            {goal.icon || '🎯'}
          </div>
          <div className="flex-1">
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{goal.title}</h1>
            {goal.description && <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{goal.description}</p>}
            <div className="flex items-center gap-3 mt-3">
              <WorkStatusBadge status={goal.status} size="md" />
              <PriorityChip priority={goal.priority} />
              <DueDateLabel date={goal.due_date} />
              <UserBadge owner={goal.owner} size="sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics strip */}
      <div className={`grid grid-cols-4 gap-4`}>
        {[
          { label: 'Projects', value: goal.project_count ?? 0 },
          { label: 'Progress', value: '—' },
          { label: 'Start', value: goal.start_date ? new Date(goal.start_date).toLocaleDateString() : '—' },
          { label: 'Due', value: goal.due_date ? new Date(goal.due_date).toLocaleDateString() : '—' },
        ].map(m => (
          <div key={m.label} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{m.label}</div>
            <div className={`text-xl font-semibold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{m.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GoalDetail;
